import {
  AttachmentsInitial,
  AttachmentsPayload,
  IAttachmentPayloadArguments,
} from "modules/Attachments/Attachments.types";
import {
  FullIssueType,
  IAISuggestions,
  IIssueRating,
  IIssueRatingCategory,
  ISelectOption,
} from "types";

export enum ISSUE_FIELD_NAMES {
  TITLE = "title",
  LOCATION = "location",
  DETAILS = "details",
  ORIGIN = "origin",
  IMPORTANCE = "importance",
  ATTACHMENTS = "attachments",
  TAGS = "tags",

  SUGGESTED_TITLE = "suggestedTitle",
  SUGGESTED_LOCATION = "suggestedLocation",
  SUGGESTED_DETAILS = "suggestedDetails",
  SUGGESTED_ORIGIN = "suggestedOrigin",
  SUGGESTED_IMPORTANCE = "suggestedImportance",
}

export class IssueInitial extends AttachmentsInitial {
  [ISSUE_FIELD_NAMES.TITLE] = "";
  [ISSUE_FIELD_NAMES.DETAILS] = "";
  [ISSUE_FIELD_NAMES.ORIGIN] = "";
  [ISSUE_FIELD_NAMES.IMPORTANCE] = "";
  [ISSUE_FIELD_NAMES.LOCATION]: ISelectOption<string, string> | null = null;
  [ISSUE_FIELD_NAMES.TAGS]: string[] = [];

  constructor(
    issue: FullIssueType | undefined,
    issueRatingCategory: Partial<IIssueRatingCategory>[] | undefined,
    issueRatings:
      | Partial<IIssueRating<Partial<IIssueRatingCategory>>>[]
      | undefined,
    AISuggestionReview?: IAISuggestions
  ) {
    super(issue?.attachments);
    if (issue) {
      this.addTitle(issue.title);
      this.addDetails(issue.details);
      this.addOrigin(issue.origin);
      this.addImportance(issue.importance);
      this.addLocation(issue.location);
      this.addTags(issue.tags);
    }

    issueRatingCategory?.forEach((c) => {
      if (c.category) {
        this[c.category] = 0; //set default value for all categories
      }
    });
    issueRatings?.forEach((r) => {
      if (r.issueRatingCategory && r.issueRatingCategory.category) {
        this[r.issueRatingCategory.category] = r.rate; //fill categories with values from issueRatings
      }
    });
  }
  addTitle(title: FullIssueType["title"]) {
    if (title) {
      this[ISSUE_FIELD_NAMES.TITLE] = title;
    }
  }
  addDetails(details: FullIssueType["details"]) {
    if (details) {
      this[ISSUE_FIELD_NAMES.DETAILS] = details;
    }
  }
  addOrigin(origin: FullIssueType["origin"]) {
    if (origin) {
      this[ISSUE_FIELD_NAMES.ORIGIN] = origin;
    }
  }
  addImportance(importance: FullIssueType["origin"]) {
    if (importance) {
      this[ISSUE_FIELD_NAMES.IMPORTANCE] = importance;
    }
  }
  addLocation(location: FullIssueType["location"]) {
    let description = "";
    if (location?.country) {
      description = location.country;
    }
    if (location?.city) {
      description = description + ", " + location.city;
    }
    if (location?.administrativeArea) {
      description = description + ", " + location.administrativeArea;
    }

    if (location?.route) {
      description = description + ", " + location.route;
    }
    if (location?.streetNumber) {
      description = description + ", " + location.streetNumber;
    }
    const fallbackLabel = description || location?.formattedAddress || "";
    const resolvedLabel =
      location?.displayLabel ||
      (typeof (location as any)?.label === "string"
        ? (location as any).label
        : fallbackLabel);
    this.location = {
      label: resolvedLabel,
      value: location?.placeId || "",
      displayLabel: resolvedLabel,
    };
  }
  addTags(tags: FullIssueType["tags"]) {
    if (tags) {
      this[ISSUE_FIELD_NAMES.TAGS] = tags;
    }
  }
}

export class IssuePayload extends AttachmentsPayload {
  [ISSUE_FIELD_NAMES.TITLE] = "";
  [ISSUE_FIELD_NAMES.DETAILS] = "";
  [ISSUE_FIELD_NAMES.ORIGIN] = "";
  [ISSUE_FIELD_NAMES.LOCATION]: {
    displayLabel?: string;
    lat?: number;
    lng?: number;
  } | null | undefined;
  [ISSUE_FIELD_NAMES.IMPORTANCE] = "";
  placeId: string | null | undefined = "";
  issueRatingCategory: Partial<IIssueRatingCategory>[] = [];

  constructor(
    {
      location,
      attachments: attachmentList,
      attachmentsInput,
      attachmentsQuill,
      attachmentsTitle,
      attachmentsType,
      ...issue
    }: Partial<IssueInitial> & Partial<IAttachmentPayloadArguments>,
    issueRatingCategory: Partial<IIssueRatingCategory>[] | undefined
  ) {
    super({
      attachmentsInput,
      attachmentsQuill,
      attachmentsTitle,
      attachmentsType,
      attachments: attachmentList,
    });
    if (issueRatingCategory) {
      this.issueRatingCategory = issueRatingCategory;
    }
    //
    Object.entries(issue).forEach(([key, value]) => {
      this[key] = value;
    });

    const providedLocation = location as Partial<
      ISelectOption<string | string[], string> & {
        lat?: number | string;
        lng?: number | string;
        formattedAddress?: string;
        placeId?: string;
      }
    > | null;

    const resolvedDisplayLabel =
      providedLocation?.displayLabel ||
      (typeof providedLocation?.label === "string"
        ? providedLocation.label
        : undefined);

    const normalizedDisplayLabel = resolvedDisplayLabel?.trim();
    const parseCoordinate = (value?: number | string) => {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed.length) {
          return undefined;
        }
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : undefined;
      }
      return undefined;
    };

    const lat = parseCoordinate(providedLocation?.lat);
    const lng = parseCoordinate(providedLocation?.lng);

    if (normalizedDisplayLabel || lat !== undefined || lng !== undefined) {
      this.location = {
        displayLabel: normalizedDisplayLabel || undefined,
        lat,
        lng,
      };
    }

    const rawPlaceId = providedLocation?.value;
    const normalizedPlaceId = Array.isArray(rawPlaceId)
      ? rawPlaceId[0]
      : typeof rawPlaceId === "string"
      ? rawPlaceId
      : undefined;
    const trimmedPlaceId = normalizedPlaceId?.trim();

    const fallbackPlaceId = (
      providedLocation as { placeId?: string } | null
    )?.placeId?.trim();

    this.placeId = trimmedPlaceId || fallbackPlaceId || "";
    //
  }
  getIssueRatings(issueId: string | undefined) {
    const issueRatings: {
      rate: Range;
      issueRatingCategory: string;
      issue: string | undefined;
    }[] = [];
    Object.entries(this).forEach(([key, value]) => {
      const cat = this.issueRatingCategory.find((i) => i.category === key);
      const rawValue =
        typeof value === "number"
          ? value
          : Number.isFinite(Number(value))
          ? Number(value)
          : undefined;
      const roundedValue =
        rawValue !== undefined && !Number.isNaN(rawValue)
          ? Math.round(rawValue)
          : undefined;
      const allowedRatings = [0, 1, 2, 3, 4, 5] as const;
      const normalizedValue =
        roundedValue !== undefined
          ? (allowedRatings.find((rating) => rating === roundedValue) as Range | undefined)
          : undefined;
      const isPositiveRating =
        normalizedValue !== undefined && Number(normalizedValue) > 0;
      if (cat && isPositiveRating) {
        issueRatings.push({
          rate: normalizedValue,
          issueRatingCategory: cat.id as string,
          issue: issueId,
        });
      }
    });
    return issueRatings;
  }
  getIssue() {
    const issue: Partial<FullIssueType> = {};
    Object.entries(this).forEach(([key, value]) => {
      if (
        (Object.values(ISSUE_FIELD_NAMES).includes(key as ISSUE_FIELD_NAMES) ||
          key === "placeId") &&
        key !== ISSUE_FIELD_NAMES.ATTACHMENTS &&
        !key.includes("SUGGESTED".toLocaleLowerCase())
      ) {
        issue[key] = value;
      }
    });
    return issue;
  }
}
